import { supabase } from '@/integrations/supabase/client';
import { isReachJobActive } from '@/services/data/reach/ReachJobService';

export type ArticleOperation = 'draft' | 'revise' | 'recheck' | 'factcheck' | 'address';

/**
 * One saved full revision of an article draft — read back from
 * `private.article_job_view`'s `revisions` key, which derives from
 * `reach_job_steps`' `draft_N` steps. Up to 3 per job (ordinals 0-2).
 * `isCurrent` compares against the TOPIC's current article, so it stays true
 * after `articleJobService.promote` switches versions.
 */
export interface ArticleRevision {
  ordinal: number;
  messageId: string;
  documentId: string;
  contentHash: string;
  createdAt: string;
  isCurrent: boolean;
  /**
   * `address`: the version an "Address review notes" pass made (with how many notes it edited and how many it left);
   * `address_before`: the version that pass started from. Absent for a revision the writer made.
   */
  kind?: 'address' | 'address_before';
  edits?: number;
  rejected?: number;
}

export interface ArticleReviewJob {
  id: string;
  topicId: string;
  operation: ArticleOperation;
  status: string;
  currentStage: string | null;
  errorMessage: string | null;
  completedAt: string | null;
  resultMessageId: string | null;
  resultDocumentId: string | null;
  latestMessageId?: string | null;
  latestDocumentId?: string | null;
  steps: Array<{
    stepKey: string;
    label: string;
    status: string;
    finding: string | null;
    /** What the step recorded. Present only when the job was read with details (one topic's job). */
    detail?: Record<string, unknown> | null;
  }>;
  revisions?: ArticleRevision[];
  /** The instruction a `revise` run was given, so a retry does not start from an empty box. */
  feedback?: string | null;
}
export type ArticleJobRequest = {
  workspaceId: string;
  topicId: string;
  requestKey: string;
} & (
  | { operation: 'draft'; expectedArtifactId: null }
  | {
      operation: 'recheck' | 'revise' | 'factcheck';
      documentId: string;
      contentHash: string;
      feedback?: string;
      /** `recheck` only: ask the model for a fresh editorial critique even though the text has not changed. */
      refreshCritique?: boolean;
    }
  | {
      /** Rewrite only the passages the open review notes and the wrong-law claims quote. */
      operation: 'address';
      documentId: string;
      contentHash: string;
      scope?: 'all' | 'claims';
    }
);

async function request<T>(path: string, body?: unknown): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sign in to work on this article.');
  const response = await fetch(path, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 409 && typeof payload.existingJobId === 'string')
      return { jobId: payload.existingJobId, existing: true } as T;
    throw new Error(
      typeof payload.error === 'string'
        ? payload.error
        : typeof payload.message === 'string'
          ? payload.message
          : 'The article request could not be completed.',
    );
  }
  return payload as T;
}

export const articleJobService = {
  getById: (workspaceId: string, jobId: string) =>
    request<{ job: ArticleReviewJob | null }>(
      `/api/articles/jobs?${new URLSearchParams({ workspaceId, jobId })}`,
    ),
  get: (workspaceId: string, topicId: string) =>
    request<{ job: ArticleReviewJob | null }>(
      `/api/articles/jobs?${new URLSearchParams({ workspaceId, topicId })}`,
    ),
  list: (workspaceId: string) =>
    request<{ jobs: ArticleReviewJob[]; truncated: boolean }>(
      `/api/articles/jobs?${new URLSearchParams({ workspaceId })}`,
    ),
  start: (body: ArticleJobRequest) =>
    request<{ jobId: string; existing?: boolean }>('/api/articles/jobs', body),
  stop: (jobId: string) => request(`/api/articles/jobs/${encodeURIComponent(jobId)}/cancel`, {}),
  /**
   * Report that a person saved an edit to this topic's article. The server decides whether the workspace
   * proceeds on its own and, if so, queues the recheck and claim check of the new text.
   */
  afterSavedEdit: (body: { workspaceId: string; topicId: string; documentId: string }) =>
    request<{
      state: 'queued' | 'existing' | 'skipped' | 'conflict';
      reason?: string;
      jobId?: string;
    }>('/api/articles/saved-edit', body),
  /** Make one saved revision the topic's current article. Refused while a job runs. */
  promote: (body: { topicId: string; messageId: string; documentId: string }) =>
    request<{ state: 'promoted'; messageId: string; documentId: string; version: number }>(
      '/api/articles/promote',
      body,
    ),
};

export const isArticleJobActive = (job: Pick<ArticleReviewJob, 'status'> | null | undefined) =>
  Boolean(job && isReachJobActive(job.status));

/** Internal workflow identifiers never become the user's stage labels. */
export function articleStageLabel(
  job: Pick<ArticleReviewJob, 'status' | 'currentStage' | 'operation'>,
): string {
  if (job.status === 'cancel_requested') return 'Stop requested';
  if (job.status === 'queued') return 'Queued';
  if (job.status === 'cancelled') return 'Stopped';
  if (job.status === 'failed') return 'Could not finish';
  if (job.status === 'partial') return 'Needs attention';
  if (job.status === 'succeeded') return 'Completed';
  if (job.status === 'awaiting_approval') return 'Waiting for review';
  // Revision steps are persisted as `draft_0`, `review_0`, `draft_1`, etc.
  // The suffix is identity, not a user-facing stage. Without normalizing it,
  // a real review step missed the map below and fell back to "Writing article",
  // directly contradicting the live row that said "Check and critique".
  const stage = (job.currentStage ?? '').replace(/_\d+$/, '');
  if (job.operation === 'factcheck' && stage === 'persist') return 'Saving the claim check';
  if (job.operation === 'address')
    return (
      {
        targets: 'Finding the review notes',
        rewrite: 'Rewriting the quoted passages',
        persist: 'Saving the new version',
        draft: 'Saving the new version',
      }[stage] ?? 'Addressing review notes'
    );
  const labels: Record<string, string> = {
    context: 'Preparing sources and brand voice',
    research: 'Gathering evidence',
    brief: 'Checking the brief',
    draft: 'Writing article',
    write: 'Writing article',
    generate: 'Writing article',
    generation: 'Writing article',
    revise: 'Improving article',
    revision: 'Improving article',
    review: 'Checking and critiquing',
    critique: 'Critiquing article',
    validate: 'Checking the draft',
    checks: 'Checking the draft',
    persist: 'Saving article',
    save: 'Saving article',
    finalize: 'Finishing article',
    claims: 'Finding the claims to check',
    sources: 'Reading the cited law',
    check: 'Checking each claim',
  };
  return (
    labels[stage] ??
    (job.operation === 'factcheck'
      ? 'Checking claims against sources'
      : job.operation === 'recheck'
        ? 'Checking article'
        : job.operation === 'revise'
          ? 'Improving article'
          : 'Writing article')
  );
}

/** Compact stage wording for status cells; the detail panel uses `articleStageLabel`. */
export function articleStageStatusLabel(
  job: Pick<ArticleReviewJob, 'status' | 'currentStage' | 'operation'>,
): string {
  if (!isArticleJobActive(job)) return articleStageLabel(job);
  if (
    job.status === 'queued' ||
    job.status === 'cancel_requested' ||
    job.status === 'awaiting_approval'
  )
    return articleStageLabel(job);
  const stage = (job.currentStage ?? '').replace(/_\d+$/, '');
  if (job.operation === 'factcheck' && stage === 'persist') return 'Saving';
  if (job.operation === 'address')
    return (
      { targets: 'Finding notes', rewrite: 'Rewriting', persist: 'Saving', draft: 'Saving' }[
        stage
      ] ?? 'Addressing notes'
    );
  const labels: Record<string, string> = {
    context: 'Preparing',
    research: 'Researching',
    brief: 'Checking brief',
    draft: 'Writing',
    write: 'Writing',
    generate: 'Writing',
    generation: 'Writing',
    revise: 'Revising',
    revision: 'Revising',
    review: 'Checking draft',
    critique: 'Checking draft',
    validate: 'Checking draft',
    checks: 'Checking draft',
    persist: 'Saving',
    save: 'Saving',
    finalize: 'Finishing',
    claims: 'Finding claims',
    sources: 'Reading the law',
    check: 'Checking claims',
  };
  // An unmapped stage still names the operation: "In progress" told a reader nothing the badge's own tone did not.
  return (
    labels[stage] ??
    (job.operation === 'factcheck'
      ? 'Checking claims'
      : job.operation === 'recheck'
        ? 'Checking draft'
        : job.operation === 'revise'
          ? 'Revising'
          : job.operation === 'draft'
            ? 'Writing'
            : 'In progress')
  );
}
